import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Spinner, Alert, Modal, StatusBadge, EmptyState } from '../../components/UI';
import { useConfirm } from '../../components/ConfirmProvider';

export default function Papers() {
  const confirm = useConfirm();
  const [papers, setPapers] = useState(null);
  const [error, setError] = useState('');
  const [builder, setBuilder] = useState(null); // { id?, title, description, duration_minutes, selected: Set }
  const [allQuestions, setAllQuestions] = useState([]);
  const [departments, setDepartments] = useState([]);
  // scope: 'assigned' = only questions on this paper, 'all' = the whole bank
  const [pickerFilters, setPickerFilters] = useState({ search: '', department_id: '', scope: 'all' });
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Start dialog: pick which candidates may sit the paper. { paper, selected: Set, search }
  const [starter, setStarter] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => { load(); }, []);
  useEffect(() => { api.get('/departments').then(setDepartments).catch(e => setError(e.message)); }, []);
  async function load() {
    try { setPapers(await api.get('/papers')); } catch (e) { setError(e.message); }
  }

  async function openBuilder(paper) {
    setError('');
    // Existing papers open on their assigned questions; a new paper has none yet.
    setPickerFilters({ search: '', department_id: '', scope: paper ? 'assigned' : 'all' });
    try {
      const q = await api.get('/questions?limit=100');
      let all = q.questions;
      // Fetch remaining pages if question bank is large
      const totalPages = Math.ceil(q.total / q.limit);
      for (let p = 2; p <= totalPages; p++) {
        const more = await api.get(`/questions?limit=100&page=${p}`);
        all = all.concat(more.questions);
      }
      setAllQuestions(all);

      if (paper) {
        const detail = await api.get(`/papers/${paper.id}`);
        setBuilder({
          id: paper.id, title: detail.title, description: detail.description || '',
          duration_minutes: detail.duration_minutes,
          selected: new Set(detail.questions.map(q => q.id)),
          status: detail.status,
          attempt_count: detail.attempt_count,
        });
      } else {
        setBuilder({ title: '', description: '', duration_minutes: '', selected: new Set() });
      }
    } catch (e) { setError(e.message); }
  }

  function toggleQuestion(id) {
    setBuilder(prev => {
      const selected = new Set(prev.selected);
      if (selected.has(id)) selected.delete(id); else selected.add(id);
      return { ...prev, selected };
    });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: builder.title, description: builder.description,
        duration_minutes: builder.duration_minutes || undefined,
        question_ids: Array.from(builder.selected),
      };
      if (builder.id) await api.put(`/papers/${builder.id}`, payload);
      else await api.post('/papers', payload);
      setBuilder(null);
      await load();
    } catch (e) { setError(e.message); }
    setSaving(false);
  }

  // Starting is a two-step: pick the candidates, then go live.
  async function openStarter(p) {
    setError('');
    try {
      const [list, detail] = await Promise.all([
        users.length ? Promise.resolve(users) : api.get('/users'),
        api.get(`/papers/${p.id}`),
      ]);
      setUsers(list);
      const candidates = list.filter(u => u.role !== 'admin' && u.is_active);
      // Re-starting a paper reuses its previous assignment; a first start pre-selects everyone.
      const previous = detail.assigned_user_ids || [];
      setStarter({
        paper: p, search: '',
        selected: new Set(previous.length ? previous : candidates.map(u => u.id)),
      });
    } catch (e) { setError(e.message); }
  }

  function toggleUser(id) {
    setStarter(prev => {
      const selected = new Set(prev.selected);
      if (selected.has(id)) selected.delete(id); else selected.add(id);
      return { ...prev, selected };
    });
  }

  async function confirmStart() {
    const p = starter.paper;
    setBusyId(p.id); setError('');
    try {
      await api.post(`/papers/${p.id}/start`, { user_ids: Array.from(starter.selected) });
      setStarter(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusyId(null);
  }

  async function stopPaper(p) {
    if (!(await confirm({ title: 'Stop question paper', message: `Stop “${p.title}”? Any in-progress attempts will be force-submitted and evaluated.`, confirmText: 'Stop paper', danger: true }))) return;
    setBusyId(p.id); setError('');
    try { const res = await api.post(`/papers/${p.id}/stop`, {}); await load();
      if (res.force_submitted) window.alert(`${res.force_submitted} in-progress attempt(s) were force-submitted.`); }
    catch (e) { setError(e.message); }
    setBusyId(null);
  }

  async function removePaper(p) {
    if (!(await confirm({ title: 'Delete question paper', message: `Delete “${p.title}”? This cannot be undone.`, confirmText: 'Delete', danger: true }))) return;
    try { await api.del(`/papers/${p.id}`); await load(); } catch (e) { setError(e.message); }
  }

  if (!papers && !error) return <Spinner />;

  // Anything except a live paper is editable, attempts or not.
  const canEdit = p => p.status !== 'active';
  // Same rule for deleting: never a live paper, never one with results attached.
  const canDelete = p => p.status !== 'active' && !p.attempt_count;

  // Picker filters are display-only -- selections made under one filter survive the next.
  const search = pickerFilters.search.trim().toLowerCase();
  const visibleQuestions = allQuestions.filter(q =>
    (pickerFilters.scope === 'all' || builder?.selected.has(q.id)) &&
    (!pickerFilters.department_id || String(q.department_id) === pickerFilters.department_id) &&
    (!search || q.question_text.toLowerCase().includes(search))
  );
  const editable = !builder?.status || canEdit(builder);

  // Admins never sit an exam, and a deactivated account cannot log in to take one.
  const candidates = users.filter(u => u.role !== 'admin' && u.is_active);
  const userSearch = (starter?.search || '').trim().toLowerCase();
  const visibleCandidates = candidates.filter(u =>
    !userSearch ||
    u.name.toLowerCase().includes(userSearch) ||
    (u.employee_id || '').toLowerCase().includes(userSearch) ||
    u.mobile.includes(userSearch)
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">Exam setup</div>
          <h1>Question papers</h1>
        </div>
        <button className="btn btn-primary" onClick={() => openBuilder(null)}>Build new paper</button>
      </div>

      <Alert type="danger">{error}</Alert>

      {papers && papers.length === 0 && (
        <div className="card"><EmptyState title="No question papers yet" sub="Build one from your question bank to get started." /></div>
      )}

      {papers && papers.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Title</th><th>Status</th><th>Questions</th><th>Marks</th><th>Duration</th><th>Attempts</th><th></th></tr></thead>
            <tbody>
              {papers.map(p => (
                <tr key={p.id}>
                  <td>{p.title}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td className="mono">{p.question_count}</td>
                  <td className="mono">{p.total_marks}</td>
                  <td className="mono">{p.duration_minutes}m</td>
                  <td className="mono">{p.attempt_count}</td>
                  <td className="table-actions">
                    {canEdit(p) && <button className="btn btn-outline btn-sm" onClick={() => openBuilder(p)}>Edit</button>}
                    {!canEdit(p) && <button className="btn btn-outline btn-sm" onClick={() => openBuilder(p)}>View</button>}
                    {(p.status === 'draft' || p.status === 'stopped') && (
                      <button className="btn btn-primary btn-sm" disabled={busyId === p.id} onClick={() => openStarter(p)}>Start</button>
                    )}
                    {p.status === 'active' && (
                      <button className="btn btn-danger btn-sm" disabled={busyId === p.id} onClick={() => stopPaper(p)}>Stop</button>
                    )}
                    {canDelete(p) && <button className="btn btn-danger btn-sm" onClick={() => removePaper(p)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {builder && (
        <Modal title={builder.id ? `${editable ? 'Edit' : 'View'} paper` : 'Build new paper'} onClose={() => setBuilder(null)} wide>
          <form onSubmit={save}>
            {editable && builder.attempt_count > 0 && (
              <Alert type="warning">
                This paper already has {builder.attempt_count} attempt(s). Past results keep the questions they
                were sat with, but changing the question set now means the paper no longer matches those results.
              </Alert>
            )}
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Title</label>
                <input type="text" value={builder.title} onChange={e => setBuilder({ ...builder, title: e.target.value })}
                  required disabled={!editable} />
              </div>
              <div className="form-group">
                <label>Duration (minutes)</label>
                <input type="number" min={1} placeholder="Uses default if blank" value={builder.duration_minutes}
                  onChange={e => setBuilder({ ...builder, duration_minutes: e.target.value })}
                  disabled={!editable} />
              </div>
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <input type="text" value={builder.description} onChange={e => setBuilder({ ...builder, description: e.target.value })}
                disabled={!editable} />
            </div>

            <div className="form-group">
              <label>Select questions ({builder.selected.size} selected, {allQuestions.reduce((s, q) => builder.selected.has(q.id) ? s + Number(q.max_score) : s, 0)} marks)</label>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2, minWidth: 200 }}>
                  <input type="text" placeholder="Search question text"
                    value={pickerFilters.search}
                    onChange={e => setPickerFilters(f => ({ ...f, search: e.target.value }))} />
                </div>
                <div className="form-group">
                  <select value={pickerFilters.department_id}
                    onChange={e => setPickerFilters(f => ({ ...f, department_id: e.target.value }))}>
                    <option value="">All departments</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div className="form-group checkbox-row" style={{ gap: 'var(--sp-3)', alignItems: 'center' }}>
                  <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                    <input type="checkbox" checked={pickerFilters.scope === 'assigned'}
                      onChange={() => setPickerFilters(f => ({ ...f, scope: 'assigned' }))} />
                    Assigned
                  </label>
                  <label style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
                    <input type="checkbox" checked={pickerFilters.scope === 'all'}
                      onChange={() => setPickerFilters(f => ({ ...f, scope: 'all' }))} />
                    All
                  </label>
                </div>
              </div>
              <div className="text-sm text-muted" style={{ marginBottom: 'var(--sp-2)' }}>
                Showing {visibleQuestions.length} of{' '}
                {pickerFilters.scope === 'assigned' ? builder.selected.size : allQuestions.length}{' '}
                {pickerFilters.scope === 'assigned' ? 'assigned questions' : 'questions'}
                {(search || pickerFilters.department_id) && (
                  <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'var(--sp-2)' }}
                    onClick={() => setPickerFilters(f => ({ ...f, search: '', department_id: '' }))}>Clear filters</button>
                )}
              </div>
              <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
                <table>
                  <thead><tr><th></th><th>Question</th><th>Dept</th><th>Marks</th></tr></thead>
                  <tbody>
                    {visibleQuestions.map(q => (
                      <tr key={q.id} onClick={() => editable && toggleQuestion(q.id)} style={{ cursor: 'pointer' }}>
                        <td><input type="checkbox" checked={builder.selected.has(q.id)} readOnly disabled={!editable} /></td>
                        <td style={{ maxWidth: 320 }}>{q.question_text}</td>
                        <td className="text-sm text-muted">{q.department_name}</td>
                        <td className="mono">{q.max_score}</td>
                      </tr>
                    ))}
                    {visibleQuestions.length === 0 && (
                      <tr><td colSpan={4} className="text-sm text-muted">
                        {pickerFilters.scope === 'assigned' && builder.selected.size === 0
                          ? 'No questions assigned to this paper yet — tick "All" to pick from the bank.'
                          : 'No questions match these filters.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setBuilder(null)}>Close</button>
              {editable && (
                <button type="submit" className="btn btn-primary" disabled={saving || builder.selected.size === 0}>
                  {saving ? 'Saving…' : 'Save paper'}
                </button>
              )}
            </div>
          </form>
        </Modal>
      )}

      {starter && (
        <Modal title={`Start "${starter.paper.title}"`} onClose={() => setStarter(null)}>
          <p className="text-sm text-muted">
            Tick the candidates who may sit this exam. Only they will see it on their home page.
          </p>
          <div className="form-group">
            <input type="text" placeholder="Search name, employee ID or mobile" value={starter.search}
              onChange={e => setStarter(s => ({ ...s, search: e.target.value }))} />
          </div>
          <div className="text-sm text-muted" style={{ marginBottom: 'var(--sp-2)' }}>
            {starter.selected.size} of {candidates.length} selected
            <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'var(--sp-2)' }}
              onClick={() => setStarter(s => ({ ...s, selected: new Set(candidates.map(u => u.id)) }))}>Select all</button>
            <button type="button" className="btn btn-outline btn-sm" style={{ marginLeft: 'var(--sp-2)' }}
              onClick={() => setStarter(s => ({ ...s, selected: new Set() }))}>Clear</button>
          </div>
          <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table>
              <thead><tr><th></th><th>Name</th><th>Employee ID</th><th>Mobile</th></tr></thead>
              <tbody>
                {visibleCandidates.map(u => (
                  <tr key={u.id} onClick={() => toggleUser(u.id)} style={{ cursor: 'pointer' }}>
                    <td><input type="checkbox" checked={starter.selected.has(u.id)} readOnly /></td>
                    <td>{u.name}</td>
                    <td className="mono">{u.employee_id}</td>
                    <td className="mono">{u.mobile}</td>
                  </tr>
                ))}
                {visibleCandidates.length === 0 && (
                  <tr><td colSpan={4} className="text-sm text-muted">No active candidates match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {starter.selected.size === 0 && (
            <Alert type="warning" style={{ marginTop: 'var(--sp-4)' }}>
              No one selected &mdash; the exam will be open to every active user.
            </Alert>
          )}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={() => setStarter(null)}>Cancel</button>
            <button type="button" className="btn btn-primary" disabled={busyId === starter.paper.id} onClick={confirmStart}>
              {busyId === starter.paper.id ? 'Starting…' : 'Start exam'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
